import { html, nothing, type TemplateResult } from "lit";
import type { ViewerNoteRecord } from "../chat/message-viewer-notes.ts";
import { icons } from "../icons.ts";
import { renderMarkdownContent } from "../markdown-render.ts";

export type MessageViewerModalProps = {
  open: boolean;
  /** Raw markdown for the message. */
  markdown: string;
  /** Human label for the message (e.g. "Long message — 42 lines · 12 KB"). */
  summary: string;
  /** Saved note for this message, if any (read-only display). */
  savedNote: ViewerNoteRecord | null;
  /** Current draft in the textarea. */
  noteDraft: string;
  /** True after a successful save (set by caller). */
  justSavedAt: number | null;
  onNoteDraftChange: (next: string) => void;
  onSaveNote: () => void;
  onClose: () => void;
};

function relativeTime(epochMs: number, nowMs: number = Date.now()): string {
  const diff = Math.max(0, nowMs - epochMs);
  const sec = Math.round(diff / 1000);
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}

export function renderMessageViewerModal(
  props: MessageViewerModalProps,
): TemplateResult | typeof nothing {
  if (!props.open) {
    return nothing;
  }

  const hasSaved = Boolean(props.savedNote);
  const draftDirty = props.noteDraft.trim() !== (props.savedNote?.note ?? "");

  return html`
    <div
      class="message-viewer-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="message-viewer-title"
      @click=${(e: Event) => {
        if (e.target === e.currentTarget) {
          props.onClose();
        }
      }}
      @keydown=${(e: KeyboardEvent) => {
        if (e.key === "Escape") {
          props.onClose();
        }
      }}
    >
      <div class="message-viewer-card" @click=${(e: Event) => e.stopPropagation()}>
        <header class="message-viewer-header">
          <div class="message-viewer-title-wrap">
            <span class="message-viewer-icon" aria-hidden="true">${icons.maximize}</span>
            <div>
              <h2 id="message-viewer-title" class="message-viewer-title">Message viewer</h2>
              <p class="message-viewer-sub">${props.summary}</p>
            </div>
          </div>
          <button
            type="button"
            class="btn icon message-viewer-close"
            title="Close viewer (Esc)"
            aria-label="Close viewer"
            data-testid="message-viewer-close"
            @click=${props.onClose}
          >
            ${icons.x}
          </button>
        </header>

        <section
          class="message-viewer-body"
          data-testid="message-viewer-body"
          tabindex="0"
        >
          ${renderMarkdownContent(props.markdown)}
        </section>

        <section class="message-viewer-notes" aria-label="Notes for this message">
          <label class="message-viewer-notes__label" for="message-viewer-note">
            Notes
            <span class="message-viewer-notes__hint muted">
              ${
                hasSaved
                  ? html`Saved ${relativeTime(props.savedNote!.updatedAt)}`
                  : html`
                      Private to this browser
                    `
              }
            </span>
          </label>
          <textarea
            id="message-viewer-note"
            class="message-viewer-notes__textarea"
            data-testid="message-viewer-note-input"
            placeholder="Capture what you want to remember about this answer…"
            rows="4"
            .value=${props.noteDraft}
            @input=${(e: Event) => {
              const target = e.target as HTMLTextAreaElement;
              props.onNoteDraftChange(target.value);
            }}
            @keydown=${(e: KeyboardEvent) => {
              // Ctrl/Cmd+Enter saves and stays open (lets the user keep reading).
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                props.onSaveNote();
              }
            }}
          ></textarea>
          <div class="message-viewer-notes__actions">
            <span class="message-viewer-notes__status muted" data-testid="message-viewer-status">
              ${
                !draftDirty && hasSaved
                  ? html`Saved ${relativeTime(props.savedNote!.updatedAt)}`
                  : draftDirty
                    ? html`
                        Unsaved changes
                      `
                    : nothing
              }
              ${
                props.justSavedAt
                  ? html`
                      · <span class="message-viewer-notes__saved-flash">Saved</span>
                    `
                  : nothing
              }
            </span>
            <button
              type="button"
              class="btn primary message-viewer-notes__save"
              data-testid="message-viewer-save"
              ?disabled=${!draftDirty}
              @click=${props.onSaveNote}
            >
              ${hasSaved ? "Update note" : "Save note"}
            </button>
          </div>
        </section>
      </div>
    </div>
  `;
}
